import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Tooltip } from 'antd';
import type { Route, Plugin } from '../mockData';
import PluginConfig from './PluginConfig';

interface RouteConfigProps {
  serviceId: string;
  routes: Route[];
  plugins: Plugin[];
  onPluginChange: (routeId: string, pluginName: string, enabled: boolean) => void;
}

const RouteConfig: React.FC<RouteConfigProps> = ({
  serviceId,
  routes,
  plugins,
  onPluginChange,
}) => {
  const [editingRouteId, setEditingRouteId] = useState<string | null>(null);

  const getRoutePlugins = (route: Route) => {
    // 从 route.plugins 数组中获取插件名称列表
    const routePluginNames = route.plugins || [];
    // 从所有插件中筛选出匹配的且已启用的插件
    return plugins.filter(p => routePluginNames.includes(p.name) && p.enabled);
  };

  const getAllRoutePlugins = (route: Route) => {
    // 从 route.plugins 数组中获取插件名称列表
    const routePluginNames = route.plugins || [];
    // 从所有插件中筛选出匹配的插件（不考虑启用状态）
    return plugins.filter(p => routePluginNames.includes(p.name));
  };

  const columns = [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: '200px',
    },
    {
      title: '域名',
      dataIndex: 'hosts',
      key: 'hosts',
      render: (hosts: string[]) => (
        <div style={{ whiteSpace: 'pre-line' }}>
          {hosts.join('\n')}
        </div>
      ),
    },
    {
      title: '支持的请求方法',
      dataIndex: 'paths',
      key: 'paths',
      render: (_: any, record: Route) => (
        <div>
          {record.methods.map((method: string) => (
            <Tag key={method} color="blue">{method}</Tag>
          ))}
        </div>
      ),
    },
    {
      title: '路径',
      dataIndex: 'paths',
      key: 'paths',
      render: (paths: string[]) => (
        <div style={{ whiteSpace: 'pre-line' }}>
          {paths.join('\n')}
        </div>
      ),
    },
    {
      title: '插件',
      key: 'plugins',
      width: '120px',
      render: (_: unknown, record: Route) => {
        const enabledPlugins = getRoutePlugins(record);
        const allPlugins = getAllRoutePlugins(record);
        return (
          <Space>
            {allPlugins.length > 0 ? (
              <Tooltip title={`已启用: ${enabledPlugins.map(p => p.name).join(', ')}\n未启用: ${allPlugins.filter(p => !p.enabled).map(p => p.name).join(', ')}`}>
                <Tag color={enabledPlugins.length > 0 ? "green" : "default"}>
                  {enabledPlugins.length} 个插件
                </Tag>
              </Tooltip>
            ) : (
              <Tag color="default">无插件</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: '150px',
      render: (_: unknown, record: Route) => (
        <Space>
          <Button type="link" size="small">配置</Button>
          <Button 
            type="link" 
            size="small"
            onClick={() => setEditingRouteId(record.id === editingRouteId ? null : record.id)}
          >
            {record.id === editingRouteId ? '收起' : '插件管理'}
          </Button>
          <Button type="link" size="small" danger>删除</Button>
        </Space>
      ),
    },
  ];

  const expandedRowRender = (record: Route) => {
    const routePlugins = getAllRoutePlugins(record);
    
    return (
      <div style={{ padding: '16px 0' }}>
        <PluginConfig
          plugins={routePlugins}
          scope="route"
          serviceId={serviceId}
          routeId={record.id}
          onPluginChange={(pluginName, enabled) => {
            onPluginChange(record.id, pluginName, enabled);
          }}
        />
      </div>
    );
  };

  return (
    <Card title="路由配置">
      <Table
        columns={columns}
        dataSource={routes}
        rowKey="id"
        pagination={false}
        expandable={{
          expandedRowRender: (record) => editingRouteId === record.id ? expandedRowRender(record) : null,
          expandedRowKeys: editingRouteId ? [editingRouteId] : [],
          expandIcon: () => null
        }}
      />
    </Card>
  );
};

export default RouteConfig; 